import { useState, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Loader2, CalendarCheck, MessageCircle, Clock } from "lucide-react";
import { CONTACT, SERVICE_OPTIONS, PROPERTY_TYPES, slotsForDate } from "../../data";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = new Date().toISOString().split("T")[0];

const field = "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink placeholder:text-ink/40 focus:border-royal focus:ring-2 focus:ring-royal/15 outline-none transition-[border-color,box-shadow] duration-300";
const labelCls = "block text-xs font-semibold tracking-[0.12em] uppercase text-ink/60 mb-2";

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_type: PROPERTY_TYPES[0], service: SERVICE_OPTIONS[0], date: "", time: "", address: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [booked, setBooked] = useState([]);

  const daySlots = useMemo(() => slotsForDate(form.date), [form.date]);
  const availableSlots = useMemo(() => daySlots.filter((s) => !booked.includes(s)), [daySlots, booked]);
  const isClosed = form.date && daySlots.length === 0;
  const isFull = form.date && !isClosed && availableSlots.length === 0;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const fetchAvailability = useCallback(async (date) => {
    try {
      const res = await axios.get(`${API}/bookings/availability`, { params: { date } });
      return res.data.booked || [];
    } catch {
      return [];
    }
  }, []);

  const onDate = async (e) => {
    const date = e.target.value;
    const slots = slotsForDate(date);
    const taken = slots.length ? await fetchAvailability(date) : [];
    setBooked(taken);
    const free = slots.filter((s) => !taken.includes(s));
    setForm((f) => ({ ...f, date, time: free[0] || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.date) {
      toast.error("Preencha nome, email, telefone e data.");
      return;
    }
    if (isClosed) {
      toast.error("Ao domingo não há marcações. Escolha outro dia.");
      return;
    }
    if (isFull) {
      toast.error("Esse dia está esgotado. Escolha outra data.");
      return;
    }
    if (!form.time) {
      toast.error("Escolha uma hora disponível.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, origin: window.location.origin });
      setDone(true);
      toast.success("Marcação recebida! Entraremos em contacto em breve.");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("Esse horário acabou de ficar indisponível. Escolha outra hora.");
        const taken = await fetchAvailability(form.date);
        setBooked(taken);
        const free = daySlots.filter((s) => !taken.includes(s));
        setForm((f) => ({ ...f, time: free[0] || "" }));
      } else {
        toast.error("Ocorreu um erro. Tente novamente ou use o WhatsApp.");
      }
    } finally {
      setLoading(false);
    }
  };

  const wa = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Olá! Gostaria de marcar uma limpeza de vidros.")}`;

  return (
    <section id="marcacao" className="relative bg-white py-24 md:py-36" data-testid="booking">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-royal font-semibold mb-5">Marcação online</p>
            <h2 className="font-display font-extrabold text-ink tracking-tight text-4xl md:text-6xl leading-[1.02] mb-6">
              Peça a sua<br /><span className="font-serif-ed italic font-medium text-royal">marcação.</span>
            </h2>
            <p className="text-muted-foreground md:text-lg max-w-md mb-6">
              Escolha o serviço, a data e a hora que lhe der jeito. Confirmamos consigo por telefone ou email.
            </p>
            <div className="mb-8 rounded-2xl border border-royal/15 bg-royal/5 p-5" data-testid="booking-hours">
              <p className="flex items-center gap-2 text-sm font-semibold text-royal mb-3"><Clock size={16} /> Horário de marcações</p>
              <ul className="space-y-1.5 text-sm text-ink/75">
                <li className="flex justify-between gap-4"><span>Segunda a Sexta</span><span className="font-medium text-ink">a partir das 18h</span></li>
                <li className="flex justify-between gap-4"><span>Sábado</span><span className="font-medium text-ink">07h — 18h</span></li>
                <li className="flex justify-between gap-4"><span>Domingo</span><span className="font-medium text-ink/50">encerrado</span></li>
              </ul>
            </div>
            <div className="space-y-4">
              {["Resposta rápida, sem compromisso", "Orçamento transparente"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-ink/80">
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-royal/10 text-royal"><Check size={14} /></span>
                  {t}
                </div>
              ))}
            </div>
            <a href={wa} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 link-underline font-medium text-ink" data-testid="booking-whatsapp-link">
              <MessageCircle size={18} /> Prefere falar já? WhatsApp
            </a>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-10 shadow-[0_20px_60px_rgba(0,47,171,0.08)]">
              {done ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-16 text-center" data-testid="booking-success">
                  <span className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-royal text-white mb-6"><CalendarCheck size={30} /></span>
                  <h3 className="font-display font-bold text-2xl text-ink mb-3">Marcação recebida!</h3>
                  <p className="text-muted-foreground max-w-sm mx-auto">Obrigado, {form.name.split(" ")[0]}. Entraremos em contacto para confirmar o dia {form.date} às {form.time}.</p>
                  <button onClick={() => { setDone(false); setForm({ ...form, name: "", message: "" }); }} className="mt-8 rounded-full border border-ink/15 px-6 h-12 font-medium hover:border-royal transition-colors">Fazer outra marcação</button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="booking-form">
                  <div>
                    <label className={labelCls}>Nome</label>
                    <input className={field} value={form.name} onChange={set("name")} placeholder="O seu nome" data-testid="booking-name" />
                  </div>
                  <div>
                    <label className={labelCls}>Telefone</label>
                    <input className={field} value={form.phone} onChange={set("phone")} placeholder="9xx xxx xxx" data-testid="booking-phone" />
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" className={field} value={form.email} onChange={set("email")} placeholder="email@exemplo.pt" data-testid="booking-email" />
                  </div>
                  <div>
                    <label className={labelCls}>Tipo de imóvel</label>
                    <select className={field} value={form.property_type} onChange={set("property_type")} data-testid="booking-property">
                      {PROPERTY_TYPES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Serviço</label>
                    <select className={field} value={form.service} onChange={set("service")} data-testid="booking-service">
                      {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Data</label>
                    <input type="date" min={today} className={field} value={form.date} onChange={onDate} data-testid="booking-date" />
                  </div>
                  <div>
                    <label className={labelCls}>Hora</label>
                    <select className={field} value={form.time} onChange={set("time")} disabled={isClosed || isFull || !form.date} data-testid="booking-time">
                      {!form.date && <option value="">Escolha a data primeiro</option>}
                      {isClosed && <option value="">Encerrado ao domingo</option>}
                      {isFull && <option value="">Esgotado neste dia</option>}
                      {availableSlots.map((t) => <option key={t}>{t}</option>)}
                    </select>
                    {isClosed && <p className="mt-2 text-xs text-destructive" data-testid="booking-closed-msg">Ao domingo não há marcações. Escolha Sáb (07h-18h) ou dias de semana (a partir das 18h).</p>}
                    {isFull && <p className="mt-2 text-xs text-destructive" data-testid="booking-full-msg">Todas as horas deste dia já estão reservadas. Escolha outra data.</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Morada (opcional)</label>
                    <input className={field} value={form.address} onChange={set("address")} placeholder="Rua, número, localidade" data-testid="booking-address" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Mensagem (opcional)</label>
                    <textarea rows={3} className={`${field} h-auto`} value={form.message} onChange={set("message")} placeholder="Detalhes do trabalho, número de janelas, etc." data-testid="booking-message" />
                  </div>
                  <button type="submit" disabled={loading || isClosed || isFull} className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-royal text-white font-semibold h-14 text-base hover:bg-[#003380] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-testid="booking-submit">
                    {loading ? <><Loader2 size={20} className="animate-spin" /> A enviar...</> : <>Confirmar marcação <CalendarCheck size={20} /></>}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
